/**
 * <h44-wishlist>: header count plus a side dialog listing the saved products.
 * Each saved handle is fetched as JSON from the storefront product route
 * (root from data-root, written by Liquid) when the dialog opens.
 */
const KEY = 'h44-wishlist';
const read = () => { try { return JSON.parse(localStorage.getItem(KEY) || '[]'); } catch { return []; } };
const write = (list) => { try { localStorage.setItem(KEY, JSON.stringify(list)); } catch {} window.dispatchEvent(new CustomEvent('h44:wishlist-change')); };

class H44Wishlist extends HTMLElement {
  #ac;

  connectedCallback() {
    this.#ac = new AbortController();
    const { signal } = this.#ac;
    const dialog = this.querySelector('dialog');
    this.#count();
    this.addEventListener('click', (event) => {
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;
      if (target.closest('[data-open]')) { dialog?.showModal(); this.#render(); }
      else if (target.closest('[data-close]') || target === dialog) dialog?.close();
      else {
        const remove = target.closest('[data-remove-handle]');
        if (remove) { write(read().filter((h) => h !== remove.dataset.removeHandle)); this.#render(); }
      }
    }, { signal });
    window.addEventListener('h44:wishlist-change', () => this.#count(), { signal });
    window.addEventListener('storage', (event) => { if (event.key === KEY) this.#count(); }, { signal });
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  #count() {
    const el = this.querySelector('[data-count]');
    const n = read().length;
    if (el) { el.textContent = n > 0 ? String(n) : ''; }
  }

  async #render() {
    const listEl = this.querySelector('[data-list]');
    if (!listEl) return;
    const handles = read();
    if (!handles.length) {
      listEl.innerHTML = '';
      const p = document.createElement('li');
      p.className = 'h44-wl-empty';
      p.textContent = this.dataset.empty || '';
      listEl.append(p);
      return;
    }
    const root = (this.dataset.root || '/').replace(/\/?$/, '/');
    const format = this.dataset.moneyFormat || '';
    const intl = new Intl.NumberFormat(this.dataset.locale || undefined, { style: 'currency', currency: this.dataset.currency || 'EUR' });
    const money = { format: (value) => {
      if (!format.includes('{{')) return intl.format(value);
      const fixed = value.toFixed(2);
      const [whole, cents] = fixed.split('.');
      const dots = whole.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
      const commas = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, kind) => {
        if (kind === 'amount_with_comma_separator') return dots + ',' + cents;
        if (kind === 'amount_no_decimals') return commas;
        if (kind === 'amount_no_decimals_with_comma_separator') return dots;
        return commas + '.' + cents;
      });
    } };
    const products = await Promise.all(handles.map((h) => fetch(root + 'products/' + encodeURIComponent(h) + '.js').then((r) => (r.ok ? r.json() : null)).catch(() => null)));
    listEl.innerHTML = '';
    products.forEach((product, i) => {
      if (!product) return;
      const li = document.createElement('li');
      li.className = 'h44-wl-item';
      const link = document.createElement('a');
      link.href = product.url || root + 'products/' + handles[i];
      const img = document.createElement('img');
      if (product.featured_image) {
        const src = new URL(product.featured_image, location.href);
        src.searchParams.set('width', '300');
        img.src = src.toString();
      }
      img.alt = '';
      img.loading = 'lazy';
      link.append(img);
      const info = document.createElement('div');
      const title = document.createElement('a');
      title.href = link.href;
      title.textContent = product.title;
      const price = document.createElement('p');
      price.textContent = money.format((product.price || 0) / 100).replace(/[.,]00(?!\d)/, '');
      info.append(title, price);
      const avail = (product.variants || []).filter((v) => v.available);
      if (avail.length) {
        const buy = document.createElement('div');
        buy.className = 'h44-wl-buy';
        const single = (product.variants || []).length === 1;
        let chosen = single ? avail[0].id : null;
        if (!single) {
          const sizes = document.createElement('div');
          sizes.className = 'h44-wl-sizes';
          (product.variants || []).forEach((v) => {
            const b = document.createElement('button');
            b.type = 'button';
            b.textContent = v.title;
            b.disabled = !v.available;
            b.addEventListener('click', () => { chosen = v.id; sizes.querySelectorAll('button').forEach((x) => x.classList.toggle('is-on', x === b)); add.disabled = false; });
            sizes.append(b);
          });
          buy.append(sizes);
        }
        const add = document.createElement('button');
        add.type = 'button';
        add.className = 'h44-wl-add';
        add.textContent = single ? 'Add to bag' : 'Select a size';
        add.disabled = !single;
        if (!single) buy.querySelectorAll('.h44-wl-sizes button').forEach((x) => x.addEventListener('click', () => { add.textContent = 'Add to bag'; }));
        add.addEventListener('click', async () => {
          if (!chosen) return;
          add.disabled = true; add.textContent = 'Adding…';
          try {
            const r = await fetch(root + 'cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ items: [{ id: chosen, quantity: 1 }] }) });
            if (!r.ok) throw new Error('add');
            const data = await r.json().catch(() => ({}));
            add.textContent = 'Added';
            const dlg = this.querySelector('dialog'); if (dlg && dlg.open) dlg.close();
            const it = (data.items && data.items[0]) || {};
            this.dispatchEvent(new CustomEvent('product:added', { bubbles: true, detail: { variant: { id: chosen }, quantity: 1, cartItemKey: it.key } }));
          } catch (e) { add.textContent = 'Try again'; add.disabled = false; }
        });
        buy.append(add);
        info.append(buy);
      }
      const remove = document.createElement('button');
      remove.type = 'button';
      remove.dataset.removeHandle = handles[i];
      remove.textContent = this.dataset.remove || 'Remove';
      li.append(link, info, remove);
      listEl.append(li);
    });
  }
}

if (!customElements.get('h44-wishlist')) {
  customElements.define('h44-wishlist', H44Wishlist);
}
